import { useEffect, type RefObject } from 'react';

const MARKETING_HASH = '#marketing';

/**
 * Brings the listing's section tabs to the top of the screen whenever a `#marketing` link is used.
 *
 * "Start marketing" and "Open Marketing" are `#marketing` links elsewhere on the page. DetailTabs
 * switches the tab on `hashchange`, but on a phone the tab strip and its content can sit below the
 * fold, so the tap would look dead. Two paths are needed: `hashchange` covers the first tap and a
 * direct URL change, and a delegated click covers a tap while the URL already ends in `#marketing`
 * (after Start marketing, or arriving from a lead's "Take down posts"), which fires no event.
 */
export function useScrollToMarketingTab(target: RefObject<HTMLElement>): void {
  useEffect(() => {
    const scrollToTabs = () => {
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      requestAnimationFrame(() =>
        target.current?.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' }),
      );
    };
    const onHashChange = () => {
      if (window.location.hash === MARKETING_HASH) scrollToTabs();
    };
    const onClick = (event: MouseEvent) => {
      if (window.location.hash !== MARKETING_HASH) return; // a hash change will fire and scroll
      const link = event.target instanceof Element ? event.target.closest('a') : null;
      if (link?.getAttribute('href') === MARKETING_HASH) scrollToTabs();
    };
    window.addEventListener('hashchange', onHashChange);
    document.addEventListener('click', onClick);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      document.removeEventListener('click', onClick);
    };
  }, [target]);
}
